import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { filter, map, switchMap, catchError } from "rxjs/operators";
import { selectCustomerIpCountry } from "@nxplatform/geo-data/slices/ip-data.slice";
import { groupArrayToKeyData } from "@nxplatform/helpers/common";
import { updateEventByType } from "@nxplatform/helpers/common/event-store-update/event-initial-update";
import { compareSpecifiers, hasBoostedOdds, isEventLive, isEventLine } from "@nxplatform/helpers/event";
import { findBoostedOdds } from "@nxplatform/helpers/event/find-boosted-odds";
import { updateOrAdd } from "@nxplatform/helpers/helpers";
import { Sections, BoostedOddsStatuses } from "@nxplatform/platform-entities/sport/common-sport.entities";
import { selectOddsBoosterBackgrounds, selectOddsBoosterEnabled } from "@nxplatform/platform-settings/locale-setup.selectors";
import { __ } from "@service/api-client/http/helpers/translates";
const ODDS_BOOSTER_FEATURE_KEY = "oddsBooster";
const initialOddsBoosterState = {
  boostedOdds: [],
  banners: [],
  bannersLoaded: false,
  bannersError: null,
  events: [],
  attributes: {},
  categories: {},
  odds: {},
  competitors: {},
  leagues: [],
  results: {},
  broadcasts: [],
  tips: [],
  players: []
};
const oddsBoosterSlice = createSlice({
  name: ODDS_BOOSTER_FEATURE_KEY,
  initialState: initialOddsBoosterState,
  reducers: {
    getBoostedOddsStart: () => {
    },
    getBoostedOddsSuccess: (state, action) => {
      state.boostedOdds = action.payload;
    },
    getBoostedOddsError: () => {
    },
    getOddsBoosterBannersListStart: () => {
    },
    getOddsBoosterBannersListSuccess: (state, action) => {
      state.bannersLoaded = true;
      const { relations, items } = action.payload;
      const {
        tips = [],
        odds = {},
        broadcasts = [],
        result = {},
        attributes = {},
        league = [],
        sportCategories = [],
        competitors = [],
        events = [],
        players = []
      } = relations || {};
      state.events = events;
      state.tips = tips;
      state.odds = odds;
      state.broadcasts = broadcasts;
      state.results = result;
      state.attributes = attributes;
      state.leagues = league;
      state.competitors = groupArrayToKeyData(competitors, "id");
      state.categories = groupArrayToKeyData(
        sportCategories,
        "id"
      );
      state.banners = items;
      state.players = players;
    },
    getOddsBoosterBannersListError: (state, action) => {
      state.bannersError = action.payload;
      state.bannersLoaded = true;
    },
    updateOddsBoosterBannersList: (state, action) => {
      const { relations, ...banners } = action.payload;
      const {
        odds = {},
        broadcasts = [],
        result = {},
        attributes = {},
        league = [],
        sportCategories = [],
        competitors = [],
        events = [],
        players = []
      } = relations || {};
      const newBanners = Object.keys(banners).reduce((acc, key) => {
        return acc.concat(banners[key]);
      }, []);
      state.banners = updateOrAdd(
        state.banners,
        newBanners,
        "oddsBoosterId"
      );
      state.events = updateOrAdd(state.events, events, "id");
      Object.keys(odds).forEach((key) => {
        state.odds[key] = updateOrAdd(state.odds[key], odds[key], ["id", "specifiers"]);
      });
      competitors.forEach((competitor) => {
        state.competitors[competitor.id] = competitor;
      });
      sportCategories.forEach((category) => {
        state.categories[category.id] = category;
      });
      state.broadcasts = updateOrAdd(state.broadcasts, broadcasts, "id");
      state.players = updateOrAdd(state.players, players, "id");
      state.leagues = updateOrAdd(state.leagues, league, "id");
      Object.keys(result).forEach((key) => {
        state.results[key] = result[key];
      });
      Object.keys(attributes).forEach((key) => {
        state.attributes[key] = attributes[key];
      });
    },
    updateBoostedOdds: (state, action) => {
      const storedOdds = JSON.parse(JSON.stringify(state.boostedOdds));
      const newOdds = Object.keys(action.payload).reduce((acc, key) => {
        return acc.concat(action.payload[key]);
      }, []);
      state.boostedOdds = updateOrAdd(
        storedOdds,
        newOdds,
        ["eventId", "marketId", "outcomeId", "oddsBoosterId", "specifiers"]
      );
    },
    updateBoostedOddsEvent: (state, action) => {
      updateEventByType(state, action.payload, Sections.live);
    }
  }
});
const oddsBoosterReducer = oddsBoosterSlice.reducer;
const {
  getOddsBoosterBannersListStart,
  getOddsBoosterBannersListSuccess,
  getOddsBoosterBannersListError,
  getBoostedOddsStart,
  getBoostedOddsSuccess,
  getBoostedOddsError,
  updateOddsBoosterBannersList,
  updateBoostedOdds,
  updateBoostedOddsEvent
} = oddsBoosterSlice.actions;
const getOddsBoosterState = (rootState) => rootState[ODDS_BOOSTER_FEATURE_KEY];
const selectOddsBoosterBannersLoaded = createSelector(
  getOddsBoosterState,
  (s) => s.bannersLoaded
);
const selectOddsBoosterBanners = createSelector(
  getOddsBoosterState,
  (s) => s.banners
);
const selectOddsBoosterOdds = createSelector(
  getOddsBoosterState,
  (s) => s.odds
);
const selectBoostedOdds = createSelector(
  getOddsBoosterState,
  (s) => s.boostedOdds
);
const selectBoostedOddsByGeo = createSelector(
  selectBoostedOdds,
  (odds) => odds.filter((odd) => odd.status === BoostedOddsStatuses.active)
);
const selectBoostedOddsByGeoUniqueEvents = createSelector(
  [selectBoostedOddsByGeo],
  (s) => {
    return s.map((item) => item.eventId).filter((value, index, self) => self.indexOf(value) === index);
  }
);
const selectOddsBoosterLeagues = createSelector(
  getOddsBoosterState,
  (s) => s.leagues
);
const selectOddsBoosterEvents = createSelector(
  getOddsBoosterState,
  (s) => s.events
);
const selectOddsBoosterBannersByGeo = createSelector(
  [
    selectOddsBoosterBanners,
    selectBoostedOddsByGeo,
    selectOddsBoosterLeagues,
    selectOddsBoosterEvents
  ],
  (banners, boostedOdds, leagues, events) => {
    return (banners || []).filter((banner) => {
      return banner.outcomes.some((outcome) => {
        const boosted = boostedOdds.some(
          (odd) => odd.oddsBoosterId === banner.oddsBoosterId && odd.outcomeId === outcome.id && odd.status === BoostedOddsStatuses.active && compareSpecifiers(odd.specifiers, banner.specifiers)
        );
        return !!boosted;
      });
    }).sort((a, b) => {
      const aEvent = events.find((event) => event.id === a.eventId);
      const bEvent = events.find((event) => event.id === b.eventId);
      const { leagueId: leagueIdA } = aEvent || {};
      const { leagueId: leagueIdB } = bEvent || {};
      const aLeague = leagues.find((league) => league.id === leagueIdA);
      const bLeague = leagues.find((league) => league.id === leagueIdB);
      const { order: orderA } = aLeague || {};
      const { order: orderB } = bLeague || {};
      return orderB - orderA;
    });
  }
);
const selectOddsBoosterTips = createSelector(
  getOddsBoosterState,
  (s) => s.tips
);
const selectOddsBoosterCategories = createSelector(
  getOddsBoosterState,
  (s) => s.categories
);
const selectOddsBoosterBroadcasts = createSelector(
  getOddsBoosterState,
  (s) => s.broadcasts
);
const selectOddsBoosterCompetitors = createSelector(
  getOddsBoosterState,
  (s) => s.competitors
);
const selectOddsBoosterResults = createSelector(
  getOddsBoosterState,
  (s) => s.results
);
const selectOddsBoosterPlayers = createSelector(
  getOddsBoosterState,
  (s) => s.players
);
const selectOddsBoosterBroadcastByEventId = createSelector(
  selectOddsBoosterBroadcasts,
  (broadcasts, eventId) => eventId,
  (broadcasts, eventId) => {
    var _a;
    return (_a = broadcasts.find((broadcast) => broadcast.eventId === eventId)) == null ? void 0 : _a.link;
  }
);
const selectOddsBoosterCategoryById = createSelector(
  selectOddsBoosterCategories,
  (categories, id) => id,
  (categories, id) => categories[id]
);
const selectOddsBoosterLeagueById = createSelector(
  selectOddsBoosterLeagues,
  (leagues, id) => id,
  (leagues, id) => leagues.find((league) => league.id === id)
);
const selectOddsBoosterEventById = createSelector(
  [
    selectOddsBoosterEvents,
    selectOddsBoosterCompetitors,
    (s, eventId) => eventId
  ],
  (events, competitors, eventId) => {
    var _a, _b;
    const event = events.find((e) => e.id === eventId);
    const { competitor1Id, competitor2Id } = event || {};
    const team1 = ((_a = competitors[competitor1Id]) == null ? void 0 : _a.name) || __("Default_._Text_._Unknown");
    const team2 = ((_b = competitors[competitor2Id]) == null ? void 0 : _b.name) || __("Default_._Text_._Unknown");
    return { ...event, team1, team2 };
  }
);
const selectOddsBoosterBannerByEventId = createSelector(
  selectOddsBoosterBannersByGeo,
  (banners, eventId) => eventId,
  (banners, eventId) => (banners || []).find((banner) => banner.eventId === eventId)
);
const selectBoostedOddsBannerMarket = createSelector(
  [selectOddsBoosterBannerByEventId, selectOddsBoosterOdds, selectBoostedOddsByGeo],
  (banner, odds, boostedOdds) => {
    var _a;
    const { eventId, specifiers } = banner || {};
    const market = (_a = odds[eventId]) == null ? void 0 : _a.find(
      (m) => +m.id === +banner.marketId && compareSpecifiers(m.specifiers, specifiers)
    );
    if (market) {
      const outcomeData = banner.outcomes.map((outcome) => {
        var _a2;
        const outcomeValues = (_a2 = market.outcomes) == null ? void 0 : _a2.find(
          (odd) => Number(odd.id) === Number(outcome.id)
        );
        return { ...outcomeValues, name: outcome.name };
      });
      const hasBoostedValues = hasBoostedOdds(boostedOdds, market, banner.eventId);
      if (hasBoostedValues) {
        return {
          ...market,
          outcomes: outcomeData
        };
      }
    }
    return null;
  }
);
const selectOddsBoosterEventOutcomes = createSelector(
  selectOddsBoosterOdds,
  (odds, eventId) => eventId,
  (odds, eventId, marketId) => marketId,
  (odds, eventId, marketId, specifiers) => specifiers,
  (odds, eventId, marketId, specifiers) => {
    var _a;
    const market = (_a = odds[eventId]) == null ? void 0 : _a.find(
      (m) => +m.id === +marketId && compareSpecifiers(m.specifiers, specifiers)
    );
    return market == null ? void 0 : market.outcomes;
  }
);
const selectOddsBoosterOddsByEventId = createSelector(
  selectOddsBoosterOdds,
  (odds, eventId) => eventId,
  (odds, eventId) => !!odds && odds[eventId]
);
const selectBoostedOddsOutcomes = createSelector(
  selectBoostedOddsByGeo,
  (boostedOdds, eventId) => eventId,
  (boostedOdds, eventId, marketId) => marketId,
  (boostedOdds, eventId, marketId, outcomeId) => outcomeId,
  (boostedOdds, eventId, marketId, outcomeId, specifiers) => specifiers,
  (boostedOdds, eventId, marketId, outcomeId, specifiers) => {
    return findBoostedOdds(
      boostedOdds,
      eventId,
      marketId,
      outcomeId,
      specifiers
    );
  }
);
const selectOddsBoosterTipsByOutcomes = createSelector(
  selectOddsBoosterTips,
  (tips, eventId) => eventId,
  (tips, eventId, market) => market,
  (tips, eventId, market) => {
    var _a;
    const eventTips = ((_a = tips.find((tip) => Number(tip.eventId) === Number(eventId))) == null ? void 0 : _a.tips) || [];
    if (market == null ? void 0 : market.outcomes) {
      return market.outcomes.reduce((acc, outcome) => {
        const tip = eventTips.find(
          (t) => +t.marketId === +market.id && +t.outcomeId === +outcome.id && t.specifier === market.specifiers
        );
        return (tip == null ? void 0 : tip.text) || acc;
      }, null);
    }
    return null;
  }
);
const selectOddsBoosterResultsByEventId = createSelector(
  selectOddsBoosterResults,
  (results, eventId) => eventId,
  (results, eventId) => results[eventId]
);
const selectOddsBoosterBannersByState = createSelector(
  [
    selectOddsBoosterBannersByGeo,
    selectOddsBoosterEvents,
    selectOddsBoosterOdds,
    selectBoostedOddsByGeo,
    (banners, isLive) => isLive
  ],
  (banners, events, odds, boostedOdds, isLive) => {
    let bannersByState = banners;
    if (typeof isLive === "boolean") {
      bannersByState = (banners || []).filter((banner) => {
        const event = events.find((e) => e.id === banner.eventId);
        return isLive ? isEventLive(event) : isEventLine(event);
      });
    }
    return (bannersByState || []).filter((banner) => {
      return !!selectBoostedOddsBannerMarket.resultFunc(banner, odds, boostedOdds);
    });
  }
);
const selectOddsBoosterBackgroundBySportId = createSelector(
  [
    selectOddsBoosterBackgrounds,
    selectOddsBoosterBannerByEventId,
    (s, eventId, sportId) => sportId
  ],
  (backgrounds, banner, sportId) => {
    return (banner == null ? void 0 : banner.bannerUrl) || backgrounds[sportId] || backgrounds.default;
  }
);
const selectEventHasOddsBooster = createSelector(
  [
    selectBoostedOddsByGeo,
    (s, eventId) => eventId,
    (s, eventId, outcomes) => outcomes
  ],
  (s, eventId, outcomes) => {
    if (outcomes) {
      const boostedOddsForEvent = (s || []).filter(
        (odd) => odd.eventId === eventId
      );
      const filteredOdds = boostedOddsForEvent.filter((o) => {
        const market = outcomes.find(
          (m) => +m.id === +o.marketId && compareSpecifiers(m.specifiers, o.specifiers)
        );
        if (market) {
          return hasBoostedOdds(s, market, eventId);
        }
        return false;
      });
      return filteredOdds.length > 0;
    }
    return false;
  }
);
const selectOddsBoosterBannersEventsIds = createSelector(
  selectOddsBoosterEvents,
  (events) => {
    return events.map((event) => Number(event.id));
  }
);
const selectOddsBoosterEventExist = createSelector(
  selectOddsBoosterEvents,
  (s, data) => data.payload,
  (s, data) => !!s.find((entity) => Number(entity.id) === Number(data.eventId))
);
const selectOddsBoosterMarketExist = createSelector(
  selectOddsBoosterOdds,
  (odds, marketId) => marketId,
  (odds, marketId, specifier) => specifier,
  (odds, marketId, specifier) => {
    return Object.values(odds).some((eventOdds) => {
      return eventOdds.some((odd) => Number(odd.id) === Number(marketId) && odd.specifiers === specifier);
    });
  }
);
const fetchBoostedOdds = () => ({
  type: getBoostedOddsStart.type
});
const fetchBoostedOddsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBoostedOddsStart.type),
  filter(() => selectOddsBoosterEnabled(state$.value)),
  map(() => selectCustomerIpCountry(state$.value)),
  switchMap((countryCode) => Api.sport.getBoostedOdds({ countryCode }).pipe(
    map((data) => getBoostedOddsSuccess(data)),
    catchError(() => of(getBoostedOddsError()))
  ))
);
const fetchOddsBoosterBanners = () => ({
  type: getOddsBoosterBannersListStart.type
});
const fetchOddsBoosterBannersEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getOddsBoosterBannersListStart.type),
  filter(() => selectOddsBoosterEnabled(state$.value)),
  map(() => selectCustomerIpCountry(state$.value)),
  switchMap((countryCode) => Api.sport.getOddsBoosterBanners({ countryCode }).pipe(
    map((data) => getOddsBoosterBannersListSuccess(data)),
    catchError((err) => of(getOddsBoosterBannersListError(err)))
  ))
);
export {
  ODDS_BOOSTER_FEATURE_KEY,
  fetchBoostedOdds,
  fetchBoostedOddsEpic,
  fetchOddsBoosterBanners,
  fetchOddsBoosterBannersEpic,
  getBoostedOddsError,
  getBoostedOddsStart,
  getBoostedOddsSuccess,
  getOddsBoosterBannersListError,
  getOddsBoosterBannersListStart,
  getOddsBoosterBannersListSuccess,
  getOddsBoosterState,
  initialOddsBoosterState,
  oddsBoosterReducer,
  oddsBoosterSlice,
  selectBoostedOdds,
  selectBoostedOddsBannerMarket,
  selectBoostedOddsByGeo,
  selectBoostedOddsByGeoUniqueEvents,
  selectBoostedOddsOutcomes,
  selectEventHasOddsBooster,
  selectOddsBoosterBackgroundBySportId,
  selectOddsBoosterBannerByEventId,
  selectOddsBoosterBanners,
  selectOddsBoosterBannersByGeo,
  selectOddsBoosterBannersByState,
  selectOddsBoosterBannersEventsIds,
  selectOddsBoosterBannersLoaded,
  selectOddsBoosterBroadcastByEventId,
  selectOddsBoosterBroadcasts,
  selectOddsBoosterCategories,
  selectOddsBoosterCategoryById,
  selectOddsBoosterCompetitors,
  selectOddsBoosterEventById,
  selectOddsBoosterEventExist,
  selectOddsBoosterEventOutcomes,
  selectOddsBoosterEvents,
  selectOddsBoosterLeagueById,
  selectOddsBoosterLeagues,
  selectOddsBoosterMarketExist,
  selectOddsBoosterOdds,
  selectOddsBoosterOddsByEventId,
  selectOddsBoosterPlayers,
  selectOddsBoosterResults,
  selectOddsBoosterResultsByEventId,
  selectOddsBoosterTips,
  selectOddsBoosterTipsByOutcomes,
  updateBoostedOdds,
  updateBoostedOddsEvent,
  updateOddsBoosterBannersList
};
